import { useEffect } from 'react';
import type Map from 'ol/Map';
import { fromLonLat, toLonLat, transform } from 'ol/proj';

declare global {
  interface Window {
    __olMap?: Map;
    __olProj?: {
      fromLonLat: typeof fromLonLat;
      toLonLat: typeof toLonLat;
      transform: typeof transform;
    };
  }
}

/**
 * Exposes the OpenLayers map instance and projection utilities on the window object
 * for E2E testing purposes. Active in development mode and E2E test builds.
 *
 * Usage in E2E tests:
 * ```typescript
 * await page.evaluate(() => {
 *   const map = window.__olMap;
 *   const { fromLonLat } = window.__olProj!;
 *   map.getView().setCenter(fromLonLat([179, 0]));
 *   map.getView().setZoom(3);
 * });
 * ```
 *
 * For extended coordinates (beyond ±180°), use transform directly:
 * ```typescript
 * const { transform } = window.__olProj!;
 * const coord = transform([190, 5], 'EPSG:4326', 'EPSG:3857');
 * ```
 */
export function useExposeMapForTesting(map: Map | null) {
  useEffect(() => {
    // Expose for development or E2E test builds, but not actual production
    if (!import.meta.env.DEV && !import.meta.env.VITE_E2E_TEST) {
      return;
    }

    if (map) {
      window.__olMap = map;
      window.__olProj = { fromLonLat, toLonLat, transform };
    }

    return () => {
      delete window.__olMap;
      delete window.__olProj;
    };
  }, [map]);
}
